import { useState, useCallback, useMemo, useRef } from "react";
import { Plus, X, Clock, User, AlertCircle, FileText, ListChecks, Tag, Paperclip, CalendarClock, Calendar } from "lucide-react";

import { Button, DateTimePicker, Input, Label, Textarea } from "@/atoms";
import { TaskPriority } from "@/enums";
import type { AddTaskDialogProps, AddTaskFormState } from "@/interfaces";
import type { TaskLiteInterface } from "@/interfaces";
import type { UserLiteInterface } from "@/interfaces";
import { t, useCreateTask, useFormErrors, useTasksListLite } from "@/hooks";
import { runAction } from "@/lib/handle-action";
import { nowLocalDT, toLocalDT } from "@/utils";
import { tasksService } from "@/services";
import { useSprintStore } from "@/store";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
    SelectValue,
} from "@/ui";

const PRIORITY_OPTIONS = [
    { value: TaskPriority.Low, label: "Low", color: "text-blue-500" },
    { value: TaskPriority.Medium, label: "Medium", color: "text-yellow-500" },
    { value: TaskPriority.High, label: "High", color: "text-orange-500" },
    { value: TaskPriority.Critical, label: "Critical", color: "text-red-500" },
];

// Multi-select autocomplete for compound task IDs
interface CompoundTaskSelectProps {
    tasks: TaskLiteInterface[];
    values: string[];
    onChange: (ids: string[]) => void;
    disabled?: boolean;
    isLoading?: boolean;
}

const CompoundTaskSelect = ({ tasks, values, onChange, disabled, isLoading }: CompoundTaskSelectProps) => {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const selected = tasks.filter((t) => values.includes(t.id));
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return tasks.filter((t) => !values.includes(t.id) && (!q || t.title.toLowerCase().includes(q) || (t.code ?? "").toLowerCase().includes(q))).slice(0, 8);
    }, [tasks, values, query]);

    const add = (id: string) => { onChange([...values, id]); setQuery(""); };
    const remove = (id: string) => onChange(values.filter((v) => v !== id));

    return (
        <div className="flex flex-col gap-2">
            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selected.map((task) => (
                        <span key={task.id} className="flex items-center gap-1 text-xs font-medium bg-primary/10 text-primary rounded-full ps-2 pe-1 py-0.5">
                            {task.code ? `${task.code} - ${task.title}` : task.title}
                            {!disabled && (
                                <button type="button" onClick={() => remove(task.id)} className="hover:text-error transition-colors">
                                    <X className="h-3 w-3" />
                                </button>
                            )}
                        </span>
                    ))}
                </div>
            )}
            {!disabled && (
                <div className="relative">
                    <Input
                        placeholder={isLoading ? t("Loading tasks...") : t("Search tasks to add as prerequisites...")}
                        value={query}
                        disabled={isLoading}
                        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                        onBlur={() => setTimeout(() => setOpen(false), 120)}
                    />
                    {open && !isLoading && (
                        <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
                            {filtered.length > 0 ? filtered.map((task) => (
                                <button key={task.id} type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => add(task.id)}
                                    className="w-full px-3 py-2 text-start text-sm hover:bg-muted cursor-pointer flex items-center gap-2">
                                    {!!task.code && <span className="text-xs text-text-muted font-mono shrink-0">{task.code}</span>}
                                    <span className="truncate">{task.title}</span>
                                </button>
                            )) : (
                                <p className="px-3 py-2 text-xs text-text-muted italic">{query ? t("No tasks match your search.") : t("No tasks available.")}</p>
                            )}
                        </div>
                    )}
                </div>
            )}
            {disabled && selected.length === 0 && (
                <p className="text-xs text-text-muted italic">{t("No prerequisites")}</p>
            )}
        </div>
    );
};

interface UsersAutocompleteProps {
    members: UserLiteInterface[];
    values: string[];
    onChange: (ids: string[]) => void;
    placeholder?: string;
}

const UsersAutocomplete = ({ members, values, onChange, placeholder }: UsersAutocompleteProps) => {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const selected = members.filter((m) => values.includes(m.id));
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return members.filter((m) =>
            !values.includes(m.id) && (!q || m.full_name.toLowerCase().includes(q) || (m.email ?? "").toLowerCase().includes(q)),
        ).slice(0, 8);
    }, [members, values, query]);

    const addUser = (id: string) => { onChange([...values, id]); setQuery(""); };
    const removeUser = (id: string) => onChange(values.filter((v) => v !== id));

    return (
        <div className="flex flex-col gap-2">
            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selected.map((m) => (
                        <span key={m.id} className="flex items-center gap-1 text-xs font-medium bg-primary/10 text-primary rounded-full ps-2 pe-1 py-0.5">
                            {m.full_name}
                            <button type="button" onClick={() => removeUser(m.id)} className="hover:text-error transition-colors">
                                <X className="h-3 w-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
            <div className="relative">
                <Input
                    placeholder={placeholder ?? t("Search users...")}
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 120)}
                />
                {open && filtered.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
                        {filtered.map((m) => (
                            <button key={m.id} type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => addUser(m.id)}
                                className="w-full px-3 py-2 text-start text-sm hover:bg-muted cursor-pointer flex items-center gap-2">
                                <span className="text-xs font-medium bg-muted rounded-full h-5 w-5 flex items-center justify-center shrink-0">{m.avatar_initials}</span>
                                <span className="flex-1 truncate">{m.full_name}</span>
                                {m.email && <span className="text-xs text-text-muted truncate">{m.email}</span>}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};


const INITIAL_FORM_STATE: AddTaskFormState = {
    title: "",
    description: "",
    assigned_to: "",
    priority: TaskPriority.Medium,
    estimatedHours: 0,
    attachments: [],
    files: [],
    tags: [],
    requirements: [],
    start_time: nowLocalDT(),
    compound_task_ids: [],
    task_type: "standalone",
};

export const AddTaskDialog = ({ open, onOpenChange, members, addTaskLocal }: AddTaskDialogProps) => {
    const { activeSprintId } = useSprintStore();
    const { setFieldErrors, clearError, clear: clearFieldErrors, getError } = useFormErrors();
    const { createHandler: createTaskHandler, isLoading: isSubmitting } = useCreateTask({ onFieldErrors: setFieldErrors });
    const [formState, setFormState] = useState<AddTaskFormState>(INITIAL_FORM_STATE);
    const [requirementInput, setRequirementInput] = useState("");
    const [tagInput, setTagInput] = useState("");
    const [deadline, setDeadline] = useState("");
    const [notifyEnabled, setNotifyEnabled] = useState(false);
    const [notifyUserIds, setNotifyUserIds] = useState<string[]>([]);
    const { tasks: rawSprintTasks, isLoading: tasksLoading } = useTasksListLite({ sprint_id: activeSprintId ?? undefined, exclude_done: true });
    const sprintTasks = rawSprintTasks.filter((t) => t.status !== "production");
    const requirementInputRef = useRef<HTMLInputElement>(null);
    const tagInputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const submittingRef = useRef(false);

    const updateField = <K extends keyof AddTaskFormState>(field: K, value: AddTaskFormState[K]) => {
        setFormState((prev) => ({ ...prev, [field]: value }));
        clearError(field);
    };

    const handleOpenChange = useCallback((newOpen: boolean) => {
        if (newOpen) {
            setFormState({ ...INITIAL_FORM_STATE, start_time: nowLocalDT() });
            setDeadline("");
        } else {
            setFormState({ ...INITIAL_FORM_STATE, start_time: nowLocalDT() });
            setRequirementInput("");
            setTagInput("");
            setDeadline("");
            setNotifyEnabled(false);
            setNotifyUserIds([]);
            clearFieldErrors();
        }
        onOpenChange(newOpen);
    }, [onOpenChange, clearFieldErrors]);

    const addRequirement = useCallback(() => {
        const label = requirementInput.trim();
        if (!label) return;
        setFormState((prev) => ({
            ...prev,
            requirements: [...prev.requirements, { id: `req-${Date.now()}`, label }],
        }));
        setRequirementInput("");
        requirementInputRef.current?.focus();
    }, [requirementInput]);

    const removeRequirement = useCallback((id: string) => {
        setFormState((prev) => ({ ...prev, requirements: prev.requirements.filter((r) => r.id !== id) }));
    }, []);

    const addTag = useCallback(() => {
        const tag = tagInput.trim().replace(/^#/, "");
        if (!tag) return;
        setFormState((prev) => ({ ...prev, tags: prev.tags.includes(tag) ? prev.tags : [...prev.tags, tag] }));
        setTagInput("");
        tagInputRef.current?.focus();
    }, [tagInput]);

    const removeTag = useCallback((tag: string) => {
        setFormState((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
    }, []);

    const handleFileAdd = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = Array.from(e.target.files ?? []);
        e.target.value = "";
        if (!picked.length) return;
        setFormState((prev) => ({ ...prev, files: [...prev.files, ...picked] }));
    }, []);

    const removeFile = useCallback((idx: number) => {
        setFormState((prev) => ({ ...prev, files: prev.files.filter((_, i) => i !== idx) }));
    }, []);

    // dead_time validation: must be >= max(now, start_time) + estimated_hours
    const validateDeadTime = useCallback((): string | null => {
        if (!deadline) return null;
        const deadMs = new Date(deadline).getTime();
        const nowMs = Date.now();
        const startMs = formState.start_time ? Math.max(new Date(formState.start_time).getTime(), nowMs) : nowMs;
        const estimatedMs = (formState.estimatedHours || 0) * 3600 * 1000;
        const earliest = startMs + estimatedMs;
        if (deadMs < earliest) {
            const earliestDate = new Date(earliest);
            return t(`Dead time must be at least ${formState.estimatedHours}h after the start time (earliest: ${earliestDate.toLocaleString()})`);
        }
        return null;
    }, [deadline, formState.start_time, formState.estimatedHours]);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (submittingRef.current) return;

        const clientErrors: Record<string, string[]> = {};
        if (!formState.title.trim()) clientErrors.title = [t("Title is required")];
        if (!formState.description.trim()) clientErrors.description = [t("Description is required")];
        if (!formState.assigned_to) clientErrors.assigned_to = [t("Assignee is required")];
        if (!formState.estimatedHours || formState.estimatedHours <= 0) clientErrors.estimated_hours = [t("Estimated hours is required")];
        if (formState.requirements.length === 0) clientErrors.requirements = [t("At least one acceptance criterion is required")];
        if (!activeSprintId) clientErrors.sprint = [t("No active sprint selected. Please select a sprint first.")];
        const deadErr = validateDeadTime();
        if (deadErr) clientErrors.dead_time = [deadErr];
        if (Object.keys(clientErrors).length > 0) {
            setFieldErrors(clientErrors);
            return;
        }

        submittingRef.current = true;

        const toISO = (local: string | null | undefined) => local ? new Date(local).toISOString() : null;

        const created = await createTaskHandler(activeSprintId, {
            title: formState.title.trim(),
            description: formState.description.trim(),
            assigned_to: formState.assigned_to,
            priority: formState.priority,
            estimated_hours: formState.estimatedHours,
            dead_time: toISO(deadline),
            start_time: toISO(formState.start_time),
            compound_task_ids: formState.compound_task_ids.length > 0 ? formState.compound_task_ids : undefined,
            task_type: formState.task_type,
            notify_on_done: notifyEnabled,
            acceptance_criteria: formState.requirements.map((req) => req.label),
        });

        if (created) {
            if (addTaskLocal) addTaskLocal(created);

            const otherExtras = [
                ...(formState.tags.length ? [tasksService.addTagsHandler(created.id, formState.tags)] : []),
                ...formState.files.map((file) => tasksService.addAttachmentHandler(created.id, file)),
            ];
            if (otherExtras.length > 0) {
                await runAction(
                    () => Promise.all(otherExtras),
                    { errorFallback: t("Task created, but some extras failed."), context: "tasks.create-extras" },
                );
            }

            handleOpenChange(false);
        }
        submittingRef.current = false;
    }, [formState, activeSprintId, createTaskHandler, handleOpenChange, addTaskLocal, deadline, notifyEnabled, notifyUserIds, setFieldErrors, validateDeadTime]);

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <div className="flex items-center gap-2 mb-1">
                        <Plus className="h-5 w-5 text-primary" />
                        <DialogTitle>{t("Add New Task")}</DialogTitle>
                    </div>
                    <DialogDescription>{t("Create a new task and assign it to a team member")}</DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-5 mt-4">
                    {/* Title — required */}
                    <div className="space-y-2">
                        <Label htmlFor="title" className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-text-muted" />
                            {t("Title")} <span className="text-error">*</span>
                        </Label>
                        <Input
                            id="title"
                            placeholder={t("Enter task title")}
                            value={formState.title}
                            onChange={(e) => updateField("title", e.target.value)}
                        />
                        {getError("title") && <p className="text-xs text-error">{getError("title")}</p>}
                    </div>

                    {/* Description — required */}
                    <div className="space-y-2">
                        <Label htmlFor="description" className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-text-muted" />
                            {t("Description")} <span className="text-error">*</span>
                        </Label>
                        <Textarea
                            id="description"
                            placeholder={t("Enter task description...")}
                            value={formState.description}
                            onChange={(e) => updateField("description", e.target.value)}
                            rows={3}
                        />
                        {getError("description") && <p className="text-xs text-error">{getError("description")}</p>}
                    </div>

                    {/* Acceptance Criteria — optional */}
                    <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                            <ListChecks className="h-4 w-4 text-text-muted" />
                            {t("Acceptance Criteria")} <span className="text-error">*</span>
                        </Label>
                        <div className="flex gap-2">
                            <Input
                                ref={requirementInputRef}
                                placeholder={t("Add an acceptance criterion...")}
                                value={requirementInput}
                                onChange={(e) => setRequirementInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRequirement(); } }}
                                className="flex-1"
                            />
                            <Button type="button" variant="outline" onClick={addRequirement} className="gap-1.5 shrink-0">
                                <Plus className="h-4 w-4" />
                                {t("Add")}
                            </Button>
                        </div>
                        {formState.requirements.length > 0 && (
                            <div className="space-y-2 mt-2">
                                {formState.requirements.map((req) => (
                                    <div key={req.id} className="flex items-center gap-3 rounded-lg border border-border bg-muted px-3 py-2">
                                        <span className="flex-1 text-sm text-text-dark">{req.label}</span>
                                        <button type="button" onClick={() => removeRequirement(req.id)} className="p-1 rounded-full hover:bg-surface text-text-muted hover:text-error transition-colors shrink-0">
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        {getError("requirements") && <p className="text-xs text-error">{getError("requirements")}</p>}
                    </div>

                    {/* Tags — optional */}
                    <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                            <Tag className="h-4 w-4 text-text-muted" />
                            {t("Tags")} <span className="text-xs text-text-muted font-normal ms-1">{t("(optional)")}</span>
                        </Label>
                        <div className="flex gap-2">
                            <Input
                                ref={tagInputRef}
                                placeholder={t("Add a tag...")}
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                                className="flex-1"
                            />
                            <Button type="button" variant="outline" onClick={addTag} className="gap-1.5 shrink-0">
                                <Plus className="h-4 w-4" />
                                {t("Add")}
                            </Button>
                        </div>
                        {formState.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                {formState.tags.map((tag) => (
                                    <span key={tag} className="flex items-center gap-1 text-xs font-medium bg-primary/10 text-primary rounded-full px-2 py-0.5">
                                        #{tag}
                                        <button type="button" onClick={() => removeTag(tag)} className="hover:text-error transition-colors">
                                            <X className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Attachments — optional */}
                    <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                            <Paperclip className="h-4 w-4 text-text-muted" />
                            {t("Attachments")} <span className="text-xs text-text-muted font-normal ms-1">{t("(optional)")}</span>
                        </Label>
                        <label className="flex items-center gap-2 w-fit text-xs text-primary font-medium cursor-pointer hover:underline">
                            <Paperclip className="h-4 w-4" />
                            {t("Choose files")}
                            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileAdd} />
                        </label>
                        {formState.files.length > 0 && (
                            <div className="flex flex-col gap-1.5 mt-1">
                                {formState.files.map((file, idx) => (
                                    <div key={idx} className="flex items-center gap-3 rounded-lg border border-border bg-muted px-3 py-2">
                                        <Paperclip className="h-3.5 w-3.5 text-text-muted shrink-0" />
                                        <span className="flex-1 text-sm text-text-dark truncate">{file.name}</span>
                                        <span className="text-[10px] text-text-muted shrink-0">{(file.size / 1024).toFixed(1)} KB</span>
                                        <button type="button" onClick={() => removeFile(idx)} className="p-1 rounded-full hover:bg-surface text-text-muted hover:text-error transition-colors shrink-0">
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Assignee / Priority / Task Type / Estimated Hours */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="assignee" className="flex items-center gap-2">
                                <User className="h-4 w-4 text-text-muted" />
                                {t("Assigned To")} <span className="text-error">*</span>
                            </Label>
                            <Select value={formState.assigned_to} onValueChange={(v) => updateField("assigned_to", v)}>
                                <SelectTrigger id="assignee" className="w-full">
                                    <SelectValue placeholder={t("Select assignee")} />
                                </SelectTrigger>
                                <SelectContent>
                                    {members.map((member) => (
                                        <SelectItem key={member.id} value={member.id}>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-medium bg-muted rounded-full h-5 w-5 flex items-center justify-center">
                                                    {member.avatar_initials}
                                                </span>
                                                <span>{member.full_name}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {getError("assigned_to") && <p className="text-xs text-error">{getError("assigned_to")}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="priority" className="flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 text-text-muted" />
                                {t("Priority")} <span className="text-xs text-text-muted font-normal ms-1">{t("(optional)")}</span>
                            </Label>
                            <Select value={formState.priority} onValueChange={(v) => updateField("priority", v as TaskPriority)}>
                                <SelectTrigger id="priority" className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PRIORITY_OPTIONS.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            <span className={option.color}>{t(option.label)}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="taskType" className="flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 text-text-muted" />
                                {t("Task Type")} <span className="text-xs text-text-muted font-normal ms-1">{t("(optional)")}</span>
                            </Label>
                            <Select value={formState.task_type} onValueChange={(v) => updateField("task_type", v as "standalone" | "compound")}>
                                <SelectTrigger id="taskType" className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="standalone">{t("Standalone")}</SelectItem>
                                    <SelectItem value="compound">{t("Compound")}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="estimatedHours" className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-text-muted" />
                                {t("Estimated Hours")} <span className="text-error">*</span>
                            </Label>
                            <Input
                                id="estimatedHours"
                                type="number"
                                min={0.5}
                                step={0.5}
                                placeholder={t("Hours")}
                                value={formState.estimatedHours || ""}
                                onChange={(e) => {
                                    const hours = parseFloat(e.target.value) || 0;
                                    updateField("estimatedHours", hours);
                                    if (hours > 0) {
                                        const pad = (n: number) => String(n).padStart(2, "0");
                                        const base = formState.start_time ? new Date(formState.start_time) : new Date();
                                        const dead = new Date(base.getTime() + hours * 3600 * 1000 + 2 * 60 * 1000);
                                        dead.setSeconds(0, 0);
                                        const local = `${dead.getFullYear()}-${pad(dead.getMonth() + 1)}-${pad(dead.getDate())}T${pad(dead.getHours())}:${pad(dead.getMinutes())}`;
                                        setDeadline(local);
                                        clearError("dead_time");
                                    }
                                }}
                            />
                            {getError("estimated_hours") && <p className="text-xs text-error">{getError("estimated_hours")}</p>}
                        </div>
                    </div>

                    {/* Start Time / Dead Time */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="startTime" className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-text-muted" />
                                {t("Start Time")} <span className="text-xs text-text-muted font-normal ms-1">{t("(optional)")}</span>
                            </Label>
                            <DateTimePicker
                                id="startTime"
                                min={nowLocalDT()}
                                value={formState.start_time}
                                onChange={(v) => { updateField("start_time", v); clearError("start_time"); }}
                            />
                            <p className="text-[10px] text-text-muted">{t("If empty or past, task starts in Ready immediately")}</p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="deadline" className="flex items-center gap-2">
                                <CalendarClock className="h-4 w-4 text-text-muted" />
                                {t("Dead Time")} <span className="text-xs text-text-muted font-normal ms-1">{t("(optional)")}</span>
                            </Label>
                            <DateTimePicker
                                id="deadline"
                                min={nowLocalDT()}
                                value={deadline}
                                onReset={() => {
                                    const base = formState.start_time ? new Date(formState.start_time) : new Date();
                                    const dead = new Date(base.getTime() + (formState.estimatedHours || 0) * 3600 * 1000 + 2 * 60 * 1000);
                                    dead.setSeconds(0, 0);
                                    return toLocalDT(dead.toISOString());
                                }}
                                onChange={(v) => {
                                    setDeadline(v);
                                    if (v) {
                                        const deadMs = new Date(v).getTime();
                                        const nowMs = Date.now();
                                        const startMs = formState.start_time ? Math.max(new Date(formState.start_time).getTime(), nowMs) : nowMs;
                                        const earliest = startMs + (formState.estimatedHours || 0) * 3600 * 1000;
                                        if (deadMs < earliest) {
                                            setFieldErrors({ dead_time: [t(`Dead time must be at least ${formState.estimatedHours}h after start time (earliest: ${new Date(earliest).toLocaleString()})`)] });
                                        } else {
                                            clearError("dead_time");
                                        }
                                    } else {
                                        clearError("dead_time");
                                    }
                                }}
                            />
                            {getError("dead_time") && <p className="text-xs text-error">{getError("dead_time")}</p>}
                            {formState.estimatedHours > 0 && (
                                <p className="text-[10px] text-text-muted">
                                    {t(`Must be ≥ start time + ${formState.estimatedHours}h estimated`)}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Compound tasks (prerequisites) — only when task_type is compound */}
                    {formState.task_type === "compound" && (
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <ListChecks className="h-4 w-4 text-text-muted" />
                                {t("Prerequisites (Compound Tasks)")} <span className="text-xs text-text-muted font-normal ms-1">{t("(optional)")}</span>
                            </Label>
                            <p className="text-xs text-text-muted">{t("Tasks that must be done before this one can start. If set, task starts in Backlog.")}</p>
                            <CompoundTaskSelect
                                tasks={sprintTasks}
                                values={formState.compound_task_ids}
                                onChange={(ids) => updateField("compound_task_ids", ids)}
                                isLoading={tasksLoading}
                            />
                            {getError("compound_task_ids") && <p className="text-xs text-error">{getError("compound_task_ids")}</p>}
                        </div>
                    )}

                    {/* Notify others when done — only when compound */}
                    {formState.task_type === "compound" && <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="notifyEnabled"
                                checked={notifyEnabled}
                                onChange={(e) => setNotifyEnabled(e.target.checked)}
                                className="h-4 w-4 rounded border-border accent-primary"
                            />
                            <Label htmlFor="notifyEnabled" className="cursor-pointer text-sm font-medium">
                                {t("Notify others when done")}
                            </Label>
                        </div>
                        {notifyEnabled && (
                            <UsersAutocomplete
                                members={members}
                                values={notifyUserIds}
                                onChange={setNotifyUserIds}
                                placeholder={t("Search users to notify...")}
                            />
                        )}
                    </div>}

                    {getError("sprint") && <p className="text-xs text-error">{getError("sprint")}</p>}
                    <div className="flex justify-end gap-3 pt-4 border-t border-border">
                        <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
                            {t("Cancel")}
                        </Button>
                        <Button type="submit" loading={isSubmitting} className="gap-2">
                            {!isSubmitting && <Plus className="h-4 w-4" />}
                            {t("Create Task")}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};
